import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { prop, pick } from 'ramda';
import * as urlUtil from '@core/js/utils/url.util';
import { Link } from '@core/js/components/common/Link';
import { Balance } from '@core/js/components/main/Balance';
import { useComponentTranslations } from '@core/js/hooks/component';
import { CaptainUpBalance } from '@theme/js/components/main/CaptainUpBalance';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';
import { useUserSelector, useDeviceSelector, useAppSelector, usePageSelector } from '@core/js/store/hooks';

import { BalancesDropdown } from './components/BalancesDropdown';

import style from './MyBalance.scss';

export const MyBalance: React.FC = () => {
    const translate = useComponentTranslations();
    const { isDesktop, isTablet } = useDeviceSelector(pick(['isDesktop', 'isTablet']));
    const isDesktopOrTablet = isDesktop || isTablet;
    const rootClassNames = usePageSelector(prop('rootClassNames'));
    const { DTAG_MAIN_BALANCE } = useAppSelector(prop('dynamicTags'));
    const hideBalanceByUrl = urlUtil.getLocationParam('showHeaderOnlyOnMobile') === '1';
    const mainBalanceArr = useMemo(() => JSON?.parse(DTAG_MAIN_BALANCE), [DTAG_MAIN_BALANCE]);
    const [isBalancesOpen, setBalancesIsOpen] = useState(false);
    const { hideBalance, lowBalanceHighlight } = useUserSelector(pick([
        'hideBalance', 'lowBalanceHighlight'
    ]));

    const isShowCustomBalance = useMemo(() =>
            Boolean(rootClassNames?.split(' ')?.find((item: string) => item === 'isShowCustomBalance')),
        [rootClassNames]);

    const customBalance = useMemo(() =>
            mainBalanceArr?.find((item: { type: string }) => item?.type === 'custom'),
        [mainBalanceArr]);

    const defaultBalance = useMemo(() =>
            mainBalanceArr?.find((item: { type: string }) => item?.type === 'default'),
        [mainBalanceArr]);

    const balance = useMemo(() => {
        if (isShowCustomBalance) {
            return customBalance;
        }
        return defaultBalance;
    }, [defaultBalance, customBalance, isShowCustomBalance]);

    const toggleBalances = useCallback((event) => {
        if (isDesktopOrTablet) {
            event.preventDefault();
            setBalancesIsOpen(prev => !prev);
        }
    }, [isDesktopOrTablet]);

    const closeBalances = useCallback(() => {
        if (isDesktopOrTablet) {
            setBalancesIsOpen(false);
        }
    }, [isDesktopOrTablet]);

    if (!isDesktopOrTablet && hideBalanceByUrl && !hideBalance) {
        return null;
    }

    const content = (
        <>
            <div>
                <span className={style.balanceName}>{`${translate(`header.${balance?.name}`)}: `} </span>
                <Link
                    href="/casino-balances"
                    onClick={toggleBalances}
                    className={`${style.balance} ${isBalancesOpen ? style.openBalance : ''} ${lowBalanceHighlight ? style.balanceLow : ''} fn-main-header-user-balance`}
                >
                    <Suspense fallback="">
                        <Balance type="balance_calc" args={balance?.balance} />
                    </Suspense>
                </Link>
                <div className={style.coinBalance}>
                    <span className={style.coinBalanceIcon}></span>
                    <CaptainUpBalance />
                </div>
            </div>
            <BalancesDropdown
                isShowCustomBalance={isShowCustomBalance}
                isBalancesOpen={isBalancesOpen}
                closeBalances={closeBalances}
            />
        </>
    );

    return (
        <AccessibilityElement className={style.balanceWrapper}>
            {content}
        </AccessibilityElement>
    );
};
